/**
 * Utility functions for connector name normalization and display
 * These functions ensure consistent naming across the application
 * to match ETLWorkflow.jsx conventions
 */

/**
 * Get module name from connector name (for internal use)
 * Matches ETLWorkflow.jsx getModuleName function
 * @param {string} connectorName - The connector name from API
 * @returns {string} Normalized module name (salesforce, sql, hubspot, oracle, etc.)
 */
export const getModuleName = (connectorName) => {
  if (!connectorName) {
    console.warn('getModuleName: No connector name provided');
    return '';
  }
  const normalized = connectorName.toLowerCase().trim();
  
  // Check for Salesforce first (most common) - must be exact or contains 'salesforce'
  if (normalized === 'sf' || normalized === 'salesforce' || normalized.includes('salesforce')) {
    return 'salesforce';
  }
  
  // Check for SQL variants
  if (normalized.includes('mssql') || normalized.includes('sql server') || normalized.includes('sqlserver') || normalized === 'sql') {
    return 'sql';
  }
  
  // Check for HubSpot
  if (normalized.includes('hubspot') || normalized === 'hubspot') {
    return 'hubspot';
  }
  
  if (normalized.includes('mysql') || normalized === 'mysql') {
    return 'mysql';
  }
  
  // Check for Oracle (should be last to avoid false matches)
  if (normalized.includes('oracle') || normalized === 'oracle') {
    return 'oracle';
  }
  
  // Check for streaming connectors
  if (normalized === 'kafka') {
    return 'kafka';
  }
  if (normalized === 's3') {
    return 's3';
  }
  if (normalized === 'local file' || normalized === 'localfile') {
    return 'local file';
  }
  
  // Default: return the normalized name or empty string
  return normalized || '';
};

/**
 * Get display name from module name (for UI display)
 * Matches ETLWorkflow.jsx getDisplayName function
 * SourceNode/DestinationNode check: mainLabel === 'Salesforce' ? ... : mainLabel === 'HubSpot' ? ... : mainLabel === 'SQL' ? ... : oracle
 * @param {string} module - The module name (from getModuleName)
 * @returns {string} Display name (Salesforce, SQL, HubSpot, Oracle, etc.)
 */
export const getDisplayName = (module) => {
  if (!module) return 'Unknown';
  const normalized = module.toLowerCase().trim();
  
  // Handle exact matches first
  if (normalized === 'salesforce' || normalized === 'sf') return 'Salesforce';
  if (normalized === 'sql' || normalized === 'mssql') return 'SQL';
  if (normalized === 'hubspot') return 'HubSpot';
  if (normalized === 'oracle') return 'Oracle';
  if (normalized === 'mysql') return 'MySQL';
  if (normalized === 'kafka') return 'Kafka';
  if (normalized === 's3') return 'S3';
  if (normalized === 'local file' || normalized === 'localfile') return 'Local File';
  
  // Handle partial matches (for safety)
  if (normalized.includes('salesforce')) return 'Salesforce';
  if (normalized.includes('sql')) return 'SQL';
  if (normalized.includes('hubspot')) return 'HubSpot';
  if (normalized.includes('oracle')) return 'Oracle';
  if (normalized.includes('mysql')) return 'MySQL';
  if (normalized.includes('kafka')) return 'Kafka';
  if (normalized.includes('s3')) return 'S3';
  if (normalized.includes('local file') || normalized.includes('localfile')) return 'Local File';
  
  // Capitalize first letter for other modules
  return module.charAt(0).toUpperCase() + module.slice(1);
};

/**
 * Get display name directly from connector name (convenience function)
 * @param {string} connectorName - The connector name from API
 * @returns {string} Display name (Salesforce, SQL, HubSpot, Oracle, etc.)
 */
export const getDisplayNameFromConnector = (connectorName) => {
  const module = getModuleName(connectorName);
  return getDisplayName(module);
};

/**
 * Normalize connector type for API calls
 * Used in TemplateConnections.jsx
 * @param {string} connectorName - The connector name
 * @returns {string} Normalized connector type for API
 */
export const getConnectorTypeForAPI = (connectorName) => {
  if (!connectorName) return '';
  const normalized = connectorName.toLowerCase().trim();
  
  // Map common connector names to API expected format
  const connectorMap = {
    'salesforce': 'salesforce',
    'sf': 'salesforce',
    'mssql': 'mssql',
    'sql server': 'mssql',
    'sqlserver': 'mssql',
    'sql': 'mssql', // SQL maps to mssql for API
    'postgresql': 'postgresql',
    'mysql': 'mysql',
    'hubspot': 'hubspot',
    'oracle': 'oracle',
  };
  
  // Check direct match first
  if (connectorMap[normalized]) {
    return connectorMap[normalized];
  }
  
  // Check partial matches
  for (const [key, value] of Object.entries(connectorMap)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return value;
    }
  }
  
  // Return normalized version as fallback
  return normalized;
};
